import {Command, CommandProps, KeyboardShortcutCommand, Mark, markPasteRule, PasteRuleMatch} from '@tiptap/core';
import {Plugin, PluginKey} from '@tiptap/pm/state'
import {isAllowedUri, Link} from '@tiptap/extension-link';
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {EntitetType, HttpsUrl, InternlenkeMålEntitetType} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MarkType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";
import {InternlenkeAttributter, internlenkeIdSomDataTypeAttributt, internlenkeTypeSomDataTypeAttributt, internlenkeUuidSomDataTypeAttributt, LenkeAttributter, lesIdFraInternlenke, lesTypeFraInternlenke, lesUuidFraInternlenke} from "felles-ng2/modell/lenkerelasjonModellHjelper";
import {nyUuid, uuidRegexpStreng} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {InterneLenker} from "felles-ng2/tjenester/InterneLenker";
import {find} from "linkifyjs";

const debug = initDebugger('Lenke');

type Tekstmarkering = {
    type: 'text';
    anchor: number;
    head: number;
}

/** Utvider {@link Link} med kommando med diverse lenkerelaterte kommandoer og attributter for {@link InterneLenker} */
export const Lenke = (interneLenker: InterneLenker): Mark => {
    return Link.extend({

        addCommands(): LenkeKommandoer<Command> {
            return {
                ...this.parent?.(),
                
                /** Legger til en tom lenke (link mark med tom href).
                 * Det forventes at href settes via kall til {@link #setLenkeadresse} før selection (this.editor.state.selection) endres.
                 * Dersom href ikke er satt innen selection endres vil link mark fjernes.*/
                lagNyLenkeMedTomHref: () => props => {
                    const ok = props.chain().focus().setLink({href: '', class: 'tom-lenke'}).run();

                    //dersom bruker lager ny lenk ved å markere tekst og så klikke på 'ny lenke' (som vil kalle denne metode)
                    //og så flytter markør uten å angi href fjerner vi lenken (for å unngå tomme lenker)
                    if (ok) {
                        const nyLenkeMarkering: Tekstmarkering = props.state.selection.toJSON();
                        //fjerner lenken dersom href ikke har blitt satt innen selection endres
                        this.editor.once('selectionUpdate', () => {
                              const aktivMarkering: Tekstmarkering = props.editor.state.selection.toJSON();
                              //bruker props.editor.chain() og ikke props.chain() som vi hadde gjort ellers da transaksjonen som props.chain() er knyttet til nå er avsluttet
                              props.editor.chain()
                                .setTextSelection(nyLenkeMarkering.anchor)
                                .extendMarkRange(MarkType.Link)
                                .unsetLinkDersomTomHref()
                                .setTextSelection(aktivMarkering.anchor)
                                .run()
                          }
                        )
                    }
                    return ok;
                },

                /** Setter lenkeadresse på aktiv link-mark */
                setLenkeadresse: (adresse: HttpsUrl, attributter?: LenkeAttributter) => props => {
                    debug(`setLenkeadresse: ${adresse}`);
                    return props.chain()
                      .focus()
                      .extendMarkRange(MarkType.Link)
                      .updateAttributes(MarkType.Link, {...attributter, href: adresse})
                      .setTextSelection(this.editor.state.selection.to) //setter markøren der den var når bruker klikket for å redigere lenke
                      // alternativt - setter markøren til slutten av lenketeksten
                      //.command((props) => props.chain().setTextSelection(props.tr.selection.to).run())
                      .run()
                },

                /** Setter lenketeksten på aktiv link-mark */
                setLenketekst: (tekst: string) => props => {
                    debug(`setLenketekst: ${tekst}`);
                    return props.chain()
                      .extendMarkRange(MarkType.Link)
                      .insertContent(tekst)
                      .setTextSelection(this.editor.state.selection.to) //setter markøren der den var når bruker klikket for å redigere lenke
                      .run()
                },

                /** Setter lenkeadresse og lenketekst på aktiv link-mark */
                setLenkeadresseOgLenketekst: (adresse: HttpsUrl, tekst: string, attributter?: LenkeAttributter) => props => {
                    debug('setLenkeadresseOgLenketekst: %o', {adresse, tekst});
                    return props
                      .chain()
                      .focus()
                      .extendMarkRange(MarkType.Link) //marker hele lenketeksten
                      .updateAttributes(MarkType.Link, {...attributter, href: adresse})
                      .insertContent(tekst) //erstatter tekstinnholdet i lenken
                      .toggleMark(MarkType.Link) //deaktiver link-mark
                      .insertContent(' ') //legg til blankt tegn etter lenke, markøren vil nå stå utenfor/etter lenken
                      .run()
                },

                lagNyLenke: (adresse: HttpsUrl, tekst: string, attributter?: LenkeAttributter) => props => {
                    debug(`lagNyLenke: %o`, {adresse, tekst, attributter});
                    return props
                      .chain()
                      .focus()
                      .setLink({...attributter, href: adresse}) //aktiver link-mark
                      .insertContent(tekst) //legg til lenketekst
                      .toggleMark(MarkType.Link) //deaktiver link-mark
                      .insertContent(' ') //legg til blankt tegn etter lenke, markøren vil nå stå utenfor/etter lenken
                      .run()
                },

                slettLenke: () => props => {
                    debug(`slettLenke: %o`, {selection: props.state.selection});
                    return props.chain().focus().unsetLink().run();
                },

                /** Fjerner lenke dersom lenkens href er tom */
                unsetLinkDersomTomHref: () => (props: CommandProps) => {
                    //props.editor.isActive(MarkType.Link, {href: ''})) svarer true dersom Link er satt i storedMarks,
                    //men svarer av ukjent grunn false når aktivNode har link mark..
                    //sjekker derfor mot aktivNode i for å identifisere situasjoner der markør står i en a-tag
                    const aktivNode = props.state.doc.nodeAt(props.state.selection.from)
                    const linkMark = aktivNode?.marks.find(mark => mark.type.name === MarkType.Link);
                    if (
                      //detekterer at Link er satt i storedMarks
                      props.editor.isActive(MarkType.Link, {href: ''}) ||
                      //detekterer a-tag med tom href
                      (erIkkeNil(linkMark) && erTomEllerNil(linkMark.attrs.href))) {
                        debug('unsetLinkDersomTomHref: href er tom, fjerner lenke')
                        return props.chain().slettLenke().run()
                    }
                    return true;
                }
            }
        },

        /** Leser data lagret i attributter på a-tagen.
         * Leste data er beskrevet av {@link InternlenkeAttributter} og er tilgjengelig via node.attr */
        addAttributes() {
            return {
                ...this.parent?.(),

                /** Se {@link InternlenkeAttributter#dataUuid} */
                dataUuid: {
                    default: undefined,
                    parseHTML: element => lesUuidFraInternlenke(element),
                    renderHTML: attributer => internlenkeUuidSomDataTypeAttributt(attributer)
                },
                /** Se {@link InternlenkeAttributter#dataId} */
                dataId: {
                    default: undefined,
                    parseHTML: element => lesIdFraInternlenke(element),
                    renderHTML: attributer => internlenkeIdSomDataTypeAttributt(attributer)
                },
                /** Se {@link InternlenkeAttributter#dataType} */
                dataType: {
                    default: undefined,
                    parseHTML: element => lesTypeFraInternlenke(element),
                    renderHTML: attributer => internlenkeTypeSomDataTypeAttributt(attributer)
                }
            };
        },

        addPasteRules() {
            const sakslenkeRegexp = new RegExp(`\/app\/innhold\/moete\/${uuidRegexpStreng}\/sak\/(${uuidRegexpStreng})$`, 'i');
            const dokumentlenkeRegexp = new RegExp(`\/app\/visdokument\/pdf\/versjon\/(${uuidRegexpStreng})\/.*\/(.*)`, 'i');

            return [
                //merk at vi her ikke legger til '...this.parent?.()' da regelen her erstatter (og er basert på) tilsvarende regel i Link
                markPasteRule({
                    find: tekst => {
                        if (erTomEllerNil(tekst)) return [];
                        const {protocols, defaultProtocol} = this.options
                        return find(tekst)
                          .filter(lenke =>
                            lenke.isLink &&
                            this.options.isAllowedUri(lenke.value, {defaultValidate: href => !!isAllowedUri(href, protocols), protocols, defaultProtocol}))
                          .map(lenke => {
                              // gjenkjenn url til sak og lag internlenke
                              const lenkeTilSak = gjenkjennInternlenkeIHref(lenke, sakslenkeRegexp, InternlenkeMålEntitetType.sak, interneLenker);
                              if (erIkkeNil(lenkeTilSak)) return lenkeTilSak;

                              //gjenkjenn url til dokument og lag internlenke
                              const lenkeTilDokument = gjenkjennInternlenkeIHref(lenke, dokumentlenkeRegexp, InternlenkeMålEntitetType.opplastetDokumentVersjon, interneLenker, lenkeMatch => lenkeMatch[2]);
                              if (erIkkeNil(lenkeTilDokument))
                                  return lenkeTilDokument;

                              //håndter som ordinær lenke
                              return {text: lenke.value, data: {href: lenke.href}, index: lenke.start};
                          })

                    },
                    type: this.type,
                    getAttributes: match => {
                        return match.data
                    },
                })
            ]
        },

        addProseMirrorPlugins() {
            return [
                ...this.parent?.()!,
                LenkePlugin
            ];
        },

        addKeyboardShortcuts(): Record<string, KeyboardShortcutCommand> {
            return {
                'Mod-k': ({editor}) => {
                    const isLink = this.editor.isActive('link');
                    const editorChain = editor.chain().focus();
                    const command = isLink
                      ? editorChain.unsetLink()
                      : editorChain.toggleLink({href: ''});

                    return command.run();
                },
            };
        },

    })
      .configure({
          openOnClick: false,
          defaultProtocol: 'https', //brukes i vår markPasteRule
          protocols: ['https'], //brukes i vår markPasteRule
          autolink: true,
          linkOnPaste: true,
      });
}

/** Koden bruker linkify for å finne URLer i tekst, definerer her informasjonen som returneres av linkify.
 * {@link https://github.com/SoapBox/linkifyjs } */
type LinkifyLenkeinfo = ReturnType<typeof find>[number];

/**
 * Enkel wrapper rundt tiptaps markPasteRule(), forutsetter at angitt målEntitetType støttes av {@link InterneLenker#entitetUrl}.
 * @param lenke info om lenken som ble funnet av linkify
 * @param ressursstiRegexp regexp som matcher ressurssti i lenken, forventer at første subgruppe (lenkeMatch[1]) inneholder id til referert entitet
 * @param målEntitetType entitet-typen til referert entitet
 * @param interneLenker tjeneste som kalles for å lage url til referert entitet
 * @param lenketekst funksjon som returnerer lenketekst, bruker lenke.value som standard
 */
function gjenkjennInternlenkeIHref(lenke: LinkifyLenkeinfo, ressursstiRegexp: RegExp, målEntitetType: InternlenkeMålEntitetType, interneLenker: InterneLenker, lenketekst: (lenkeMatch: RegExpMatchArray, lenke: LinkifyLenkeinfo) => string = (lenkeMatch, lenke) => lenke.value): PasteRuleMatch | null {
    const lenkeMatch = lenke.href.match(ressursstiRegexp);
    if (erNil(lenkeMatch)) return null;

    const referertEntitetId = lenkeMatch[1];
    const href = interneLenker.entitetUrl(referertEntitetId, målEntitetType as any as EntitetType); //InternlenkeMålEntitetType er et subset av EntitetType
    return {
        text: lenke.value,
        data: {dataUuid: internlenkeUuidGenerator.ny(), dataId: referertEntitetId, dataType: målEntitetType, href},
        index: lenke.start,
        replaceWith: lenketekst(lenkeMatch, lenke)
    }
}


/** Lager uuid for internlenker. */
export const internlenkeUuidGenerator = {
    ny: () => nyUuid(),
}

/** regexp som matcher internlenker */
//[\s\S]+? representerer alle tegn, inklusive linjeskift
const internlenkeMatcher = new RegExp(`<a[\\s\\S]+?(data-uuid\\s*=\\s*"${uuidRegexpStreng}")[\\s\\S]*?>`, 'ig');

/** Plugin som sørger for å erstatte data-uuid i innlimt html med ny uuid.
 * Dette er nødvendig for at serversiden skal oppfatte lenken som en ny lenke.*/
const LenkePlugin = new Plugin({
    key: new PluginKey('lenke-plugin'),
    props: {
        /** finn a-tager som har data-uuid attributt, erstatt denne verdien men ny uuid */
        transformPastedHTML: (html: string): string => {
            for (let treff of html.matchAll(internlenkeMatcher)) {
                const dataUuidAttributt = treff[1];
                html = html.replace(dataUuidAttributt, `data-uuid="${internlenkeUuidGenerator.ny()}"`)
            }
            return html;
        }
    }
});


// typedefinisjoner

interface LenkeKommandoer<ReturnType> {

    /** Legger til en tom lenke (link mark med tom href).
     * Det forventes at href settes via kall til {@link #setLenkeadresse} før selection (this.editor.state.selection) endres.
     * Dersom href ikke er satt inne selection endres vil link mark fjernes. */
    lagNyLenkeMedTomHref: () => ReturnType;

    lagNyLenke: (adresse: HttpsUrl, tekst: string, attributter?: LenkeAttributter) => ReturnType;

    /** Setter lenkeadresse på aktiv link-mark */
    setLenkeadresse: (adresse: HttpsUrl, attributter?: LenkeAttributter) => ReturnType;

    /** Setter lenketeksten på aktiv link-mark */
    setLenketekst: (tekst: string) => ReturnType;

    /** Setter lenkeadresse og lenketekst på aktiv link-mark */
    setLenkeadresseOgLenketekst: (adresse: HttpsUrl, tekst: string, attributter?: LenkeAttributter) => ReturnType;

    slettLenke: () => ReturnType;

    /** Fjerner lenke dersom href er tom */
    unsetLinkDersomTomHref: () => ReturnType
}

//for at kommandoene skal dukke opp i editor ts typen
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        lenke: LenkeKommandoer<ReturnType>;
    }
}
